import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Button, TextField, Box, IconButton, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { workscopeApi } from "../apiServices";
import { globalGetService, globalPutService } from "../../../utils/globalApiServices";
import { checkApiStatus } from "../../../utils";
import { STableLoader } from "../../../shared_elements";
import { trackActivity } from "../../../utils/mixpanel";
import { PermissionWrapper } from "../../../wrappers";

const createInitialMajorModule = (index) => ({
  name: "",
  subModules: [
    {
      name: "",
      items: [{ moduleNo: "", moduleDescription: "", initialLevel: "", finalLevel: "" }]
    }
  ]
});
const WorkScopeTable = ({ shopVisit }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [workscopeData, setWorkscopeData] = useState([createInitialMajorModule(1)]);
  const [isLoading, setLoading] = useState({ skeletonLoader: false, });
  const toggleLoader = (type, status) => { setLoading({ [type]: status }) }
  const getWorkscope = () => {
    toggleLoader("skeletonLoader", true)
    globalGetService(workscopeApi(shopVisit?.slug), {}).then(response => {
      if (checkApiStatus(response)) {
        setWorkscopeData(response?.data?.data?.workscope_modules?.length > 0 ?  response?.data?.data?.workscope_modules : [
          {
            "name": "",
            "subModules": [
              {
                "name": "",
                "items": [
                  {
                    "moduleNo": "",
                    "finalLevel": "",
                    "initialLevel": "",
                    "moduleDescription": ""
                  }
                ]
              }
            ]
          }
        ])
      }
      toggleLoader("skeletonLoader", false)
    });
  };
  useEffect(() => {
    if (shopVisit?.slug) {
      getWorkscope('skeletonLoader');
    }
  }, []);
  const handleAddWorkscope = () => {
    setWorkscopeData(prev => [...prev, createInitialMajorModule(prev.length + 1)]);
  };
  const handleAddSubModule = (majorIdx) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx] = {
        ...updated[majorIdx],
        subModules: [
          ...updated[majorIdx].subModules,
          { name: "", items: [{ moduleNo: "", moduleDescription: "", initialLevel: "", finalLevel: "" }] }
        ]
      };
      return updated;
    });
  };
  const handleAddItem = (majorIdx, subIdx) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx].subModules[subIdx] = {
        ...updated[majorIdx].subModules[subIdx],
        items: [
          ...updated[majorIdx].subModules[subIdx].items,
          { moduleNo: "", moduleDescription: "", initialLevel: "", finalLevel: "" }
        ]
      };
      return updated;
    });
  };
  const handleDeleteItem = (majorIdx, subIdx, itemIdx) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx].subModules[subIdx] = {
        ...updated[majorIdx].subModules[subIdx],
        items: updated[majorIdx].subModules[subIdx].items.filter((_, idx) => idx !== itemIdx)
      };
      if (updated[majorIdx].subModules[subIdx].items.length === 0) {
        updated[majorIdx].subModules.splice(subIdx, 1);
      }
      if (updated[majorIdx].subModules.length === 0) {
        updated.splice(majorIdx, 1);
      }
      return updated;
    });
  };
  const handleMajorModuleNameChange = (majorIdx, value) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx] = { ...updated[majorIdx], name: value };
      return updated;
    });
  };
  const handleSubModuleNameChange = (majorIdx, subIdx, value) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx].subModules[subIdx] = {
        ...updated[majorIdx].subModules[subIdx], name: value
      };
      return updated;
    });
  };
  const handleInputChange = (majorIdx, subIdx, itemIdx, field, value) => {
    setWorkscopeData(prev => {
      const updated = [...prev];
      updated[majorIdx].subModules[subIdx].items[itemIdx] = {
        ...updated[majorIdx].subModules[subIdx].items[itemIdx],
        [field]: value
      };
      return updated;
    });
  };
  const cellBorder = { border: "1px solid #ccc", minWidth: "140px" };
  const addEditWorkscope = () => {
    toggleLoader("skeletonLoader", true)
    globalPutService(workscopeApi(shopVisit?.slug), { workscope_modules: workscopeData }).then((response) => {
      if (checkApiStatus(response)) {
        toggleLoader("skeletonLoader", false)
        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
        trackActivity('Item Edited', { page_title: `Edit Shop Visit WorkScope`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: workscopeData, response: response?.data })
      } else {
        toggleLoader("buttonLoader", false)
        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
        trackActivity('Item Edited', { page_title: `Edit Shop Visit WorkScope`, error_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: workscopeData, response: response?.data })
      }
    });
  }
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Shop Visit WorkScope`, page_type: page_type, })
  }
  return (
    <>
      <Paper sx={{ maxWidth: '100%', margin: "auto", p: 1, marginTop: "4px" }}>
      <Box className="text-left"   sx={{ float: "right", mb: 1 }}>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "shop_visit", operationType: ['U'] }}>
            <Button variant="contained" onClick={() => { addEditWorkscope(); handleItemClicked('Save Button') }} color="primary" size="small">
              {isLoading?.buttonLoader ? <CircularProgress color="#ffffff" size={24} /> : 'Save'}
            </Button>
          </PermissionWrapper>
        </Box>
        <Button
          onClick={() => { handleAddWorkscope(); handleItemClicked('Add Workscope Button') }}
          sx={{ float: "left", mb: 1 }}
          variant="outlined"
          color="primary"
          size="small"
        >
          + Add Workscope
        </Button>

        <Box sx={{ clear: "both", }} />
        {isLoading?.skeletonLoader ? <STableLoader count={6} rowCount={3} /> :   <TableContainer style={{ maxHeight: `${window.innerHeight - 300}px`, overflow: "auto" }}>
          <Table className='mui-table-format scrollable-table-container' stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={cellBorder}>Major Module</TableCell>
                <TableCell rowSpan={2} sx={cellBorder}>Sub Module</TableCell>
                <TableCell rowSpan={2} sx={cellBorder}>Module No</TableCell>
                <TableCell rowSpan={2} sx={cellBorder}>Module Description</TableCell>
                <TableCell colSpan={2} align="center" sx={cellBorder}>
                  Workscope Level
                </TableCell>
                <TableCell rowSpan={2} sx={cellBorder}>Action</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...cellBorder, position: "sticky", top: "36px" }}>Initial</TableCell>
                <TableCell sx={{ ...cellBorder, position: "sticky", top: "36px" }}>Final</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workscopeData?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={cellBorder}>
                    No Records Found
                  </TableCell>
                </TableRow>
              ) : (
                workscopeData?.map((majorModule, majorIdx) =>
                  majorModule?.subModules?.map((sub, subIdx) =>
                    sub?.items?.map((item, itemIdx) => (
                      <TableRow key={`${majorIdx}-${subIdx}-${itemIdx}`}>
                        {subIdx === 0 && itemIdx === 0 && (
                          <TableCell
                            rowSpan={majorModule.subModules.reduce((acc, s) => acc + s.items.length, 0)}
                            sx={{ ...cellBorder, verticalAlign: "top", minWidth: 180, background: "#f5f5f5" }}
                          >
                            <TextField
                              size="small"
                              variant="outlined"
                              value={majorModule.name}
                              onChange={e => handleMajorModuleNameChange(majorIdx, e.target.value)}
                              fullWidth
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={() => { handleAddSubModule(majorIdx); handleItemClicked('Add Sub Module Button') }}
                              >
                                + Add a Sub Module
                              </Button>
                            </Box>
                          </TableCell>
                        )}

                        {itemIdx === 0 && (
                          <TableCell
                            rowSpan={sub.items.length}
                            sx={{ ...cellBorder, minWidth: 140, verticalAlign: "top", background: "#f5f5f5" }}
                          >
                            <Box >
                              <TextField
                                size="small"
                                variant="outlined"
                                value={sub.name}
                                onChange={e =>
                                  handleSubModuleNameChange(majorIdx, subIdx, e.target.value)
                                }
                                fullWidth
                              />
                            </Box>
                          </TableCell>
                        )}

                        <TableCell sx={{ ...cellBorder, background: "#f5f5f5" }}>

                          <TextField
                            size="small"
                            variant="outlined"
                            value={item.moduleNo}
                            onChange={e =>
                              handleInputChange(
                                majorIdx,
                                subIdx,
                                itemIdx,
                                "moduleNo",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                          {itemIdx === sub.items.length - 1 && (
                            <Box sx={{ display: "flex", alignItems: "center", background: "#f5f5f5" }}>
                              <Button
                                size="small"
                                sx={{ mt: 1 }}
                                variant="outlined"
                                onClick={() => {handleAddItem(majorIdx, subIdx);handleItemClicked('Add Another Button') }}
                              >
                                + Add Another
                              </Button>

                            </Box>
                          )}

                        </TableCell>
                        <TableCell sx={{ ...cellBorder, background: "#f5f5f5" }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            value={item.moduleDescription}
                            onChange={e =>
                              handleInputChange(
                                majorIdx,
                                subIdx,
                                itemIdx,
                                "moduleDescription",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellBorder, background: "#f5f5f5" }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            value={item.initialLevel}
                            onChange={e =>
                              handleInputChange(
                                majorIdx,
                                subIdx,
                                itemIdx,
                                "initialLevel",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellBorder, background: "#f5f5f5" }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            value={item.finalLevel}
                            onChange={e =>
                              handleInputChange(
                                majorIdx,
                                subIdx,
                                itemIdx,
                                "finalLevel",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellBorder, background: "#f5f5f5" }}>
                          <IconButton
                            aria-label="Delete Row"
                            color="error"
                            onClick={() => { handleDeleteItem(majorIdx, subIdx, itemIdx); handleItemClicked('DeleteIcon') }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                )
              )}
            </TableBody>
          </Table>
        </TableContainer> }
      
      </Paper>

    </>

  );
}

export default WorkScopeTable